import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Note, NoteCategory } from '../models/note.model';
import { ApiService } from '../services/api.service';

const ROUTES = {
  get: (orderId: string) => `orders/${orderId}/notes`,
  create: (orderId: string) => `orders/${orderId}/notes`,
  update: (orderId: string, noteId: string) => `orders/${orderId}/notes/${noteId}`,
  delete: (orderId: string, noteId: string) => `orders/${orderId}/notes/${noteId}`,
  getUserNotes: (userId: string) => `users/${userId}/notes`,
  createUserNote: (userId: string) => `users/${userId}/notes`,
  updateUserNote: (userId: string, noteId: string) => `users/${userId}/notes/${noteId}`,
  deleteUserNote: (userId: string, noteId: string) => `users/${userId}/notes/${noteId}`,
};

/**
 *  Contains all of the methods required to communicate with the Notes API.
 *  Should not contain methods with business logic. This is only for API communication
 */
@Injectable({
  providedIn: 'root',
})
export class MarketingOrderNotesClient {
  private client = inject(ApiService);

  /**
   * Private helper to fetch notes from any route
   */
  private async fetchNotes(route: string, category?: NoteCategory): Promise<Note[]> {
    const params: any = category ? { category } : {};
    return lastValueFrom(this.client.query$(route, { version: 'v2', model: Note, params }));
  }

  /**
   * Private helper to create a note
   */
  private async createNote(route: string, body: Note): Promise<Note[]> {
    const notes = await this.client.create<Note[], Note>(route, body, { version: 'v2' });
    return notes.map((n) => new Note(n));
  }

  /**
   * Private helper to update a note
   */
  private async updateNote(route: string, body: Partial<Note>): Promise<Note[]> {
    const notes = await this.client.update<Note[], Partial<Note>>(route, body, { version: 'v2' });
    return notes.map((n) => new Note(n));
  }

  /**
   * Private helper to delete a note
   */
  private async deleteNote(route: string): Promise<Note[]> {
    const notes = await lastValueFrom(this.client.delete<Note[]>(route, {}, { version: 'v2' }));
    return notes.map((n) => new Note(n));
  }

  /**
   * Queries the API and returns all order notes for a provided orderId parameter
   * @param orderId - The order ID
   * @param category - Optional category filter
   */
  get(orderId: string, category?: NoteCategory): Promise<Note[]> {
    return this.fetchNotes(ROUTES.get(orderId), category);
  }

  /**
   * Queries the API and returns all agent notes for a provided userId parameter
   * @param userId - The user/agent ID
   * @param category - Optional category filter
   */
  getAgentNotes(userId: string, category?: NoteCategory): Promise<Note[]> {
    return this.fetchNotes(ROUTES.getUserNotes(userId), category);
  }

  /**
   * POSTs the notification form to the API. Ensuring proper form validation
   * @param orderId - The order ID
   * @param body - The note data to create
   */
  post(orderId: string, body: Note): Promise<Note[]> {
    return this.createNote(ROUTES.create(orderId), body);
  }

  /**
   * POSTs an agent note to the API
   * @param userId - The user/agent ID
   * @param body - The note data to create
   */
  postAgentNote(userId: string, body: Note): Promise<Note[]> {
    return this.createNote(ROUTES.createUserNote(userId), body);
  }

  /**
   * Updates an existing order note
   * @param orderId - The order ID
   * @param noteId - The note ID to update
   * @param body - The updated note data
   */
  put(orderId: string, noteId: string, body: Partial<Note>): Promise<Note[]> {
    return this.updateNote(ROUTES.update(orderId, noteId), body);
  }

  /**
   * Updates an existing agent note
   * @param userId - The user/agent ID
   * @param noteId - The note ID to update
   * @param body - The updated note data
   */
  putAgentNote(userId: string, noteId: string, body: Partial<Note>): Promise<Note[]> {
    return this.updateNote(ROUTES.updateUserNote(userId, noteId), body);
  }

  /**
   * Deletes an order note
   * @param orderId - The order ID
   * @param noteId - The note ID to delete
   */
  delete(orderId: string, noteId: string): Promise<Note[]> {
    return this.deleteNote(ROUTES.delete(orderId, noteId));
  }

  /**
   * Deletes an agent note
   * @param userId - The user/agent ID
   * @param noteId - The note ID to delete
   */
  deleteAgentNote(userId: string, noteId: string): Promise<Note[]> {
    return this.deleteNote(ROUTES.deleteUserNote(userId, noteId));
  }
}
